//================================================================
//  Component: Triage Tab
//================================================================

//  Purpose: This contains the triage information for a Procurement request task

//  Properties:
//    - task = {An object, contains the Procurement request task details}

//  Example:
//    <TriageTab
//      task={task}
//    ></TriageTab>    

// ==============================================================

// Libraries
import { useState, useReducer } from 'react';

// Contexts

// Components
import AssignTask from '../../../../Components/AssignTask/AssignTask';
import CancelRequestModal from '../Modals/CancelRequestModal';

//Functions
import ConvertDate from '../../../../Library/ConvertDate';

// Images
import Expand from '../../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../../Components/Images/Icon_Expand_Teal.svg';
import FileIcon from '../../../../Components/Images/Icon_File_Black.svg';

// CSS
import '../../Task.css'

export default function TriageTab({
    task
}) {

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to store the visibility of the cancel request modal
    const [cancelRequestModal, setCancelRequestModal] = useState(false);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store whether a section is expanded
    const [taskSection, setTaskSection] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'assignedToCollapsed': false,
            'digitalCommercialRequestCollapsed': false,
        }
    );

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col gap-2 p-0'>

            {/* =========================================================== */}
            {/*  Status Label                                               */}
            {/* =========================================================== */}

            {
                [
                    {
                        'name': 'Completed',
                        'description': <><b className='font-medium'>{task?.lastmodifiedby?.givenname} {task?.lastmodifiedby?.surname}</b> has completed this digital request on {ConvertDate(task?.lastmodifieddate)}. </>,
                        'background': '#E7F2DA',
                        'status': 'Completed',
                        'color': '#8ABB45'
                    },
                    {
                        'name': 'Cancelled',
                        'description':
                            <>
                                <b className='font-medium'>{task?.lastmodifiedby?.givenname} {task?.lastmodifiedby?.surname}</b> has cancelled this digital request on {ConvertDate(task?.lastmodifieddate)}.
                                <div className='mt-2'>
                                    <span>
                                        Reason: {task?.cancellationreason && task?.cancellationreason !== '' ? task?.cancellationreason : 'N/A'}
                                    </span>
                                </div>
                            </>,
                        'background': '#EBEBEB',
                        'status': 'Cancelled',
                        'color': '#616161'
                    },
                    {
                        'name': 'Rejected',
                        'description':
                            <>
                                <b className='font-medium'>{task?.lastmodifiedby?.givenname} {task?.lastmodifiedby?.surname}</b> has rejected this digital request on {ConvertDate(task?.lastmodifieddate)}.
                                <div className='mt-2'>
                                    <span>
                                        Reason: {task?.activityfeed?.filter((item) => item.action === 'rejected')?.comments?.length > 0 ? task?.activityfeed?.filter((item) => item.action === 'rejected')?.comments : 'N/A'}
                                    </span>
                                </div>
                            </>,
                        'background': '#FCF4F6',
                        'status': 'Rejected',
                        'color': '#C4314B'
                    },
                ]
                    ?.filter((style) => style.status === task?.status)
                    .map((object, index) => (

                        <div className='px-4 py-3 mb-2' key={index} style={{ backgroundColor: object.background, borderLeft: `5px solid ${object.color}` }}>
                            <h6 className='text-black font-medium text-[18px]'>{object.name}</h6>
                            <p className='mb-0 text-gray-600'>{object.description}</p>
                        </div>

                    ))
            }


            {/* ===================================================== */}
            {/*  Manage Request                                       */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>
                <div className='flex flex-row items-center justify-between px-[2%] py-[14px] cursor-pointer' onClick={() => setTaskSection({ 'assignedToCollapsed': !taskSection.assignedToCollapsed })}>

                    <div className='flex gap-3'>
                        <img
                            className='cursor-pointer'
                            src={taskSection?.assignedToCollapsed ? Collapse : Expand}
                            alt={'>'}
                        ></img>
                        <h4 className='text-[20px] m-0'> Manage Request </h4>
                    </div>

                    {
                        task?.status !== 'Completed' && task?.status !== 'Rejected' && task?.status !== 'Cancelled' &&
                        <div 
                            className='Secondary-Red-Button'
                            style={{ margin: '0px'}}                            
                            onClick={() => setCancelRequestModal(true)}
                        >
                            Cancel Request
                        </div>
                    }

                </div>

                <hr className='m-0' hidden={taskSection?.assignedToCollapsed}></hr>

                <div className='p-[3%]' hidden={taskSection?.assignedToCollapsed}>

                    {/* =================================== */}
                    {/*  Request ID                         */}
                    {/* =================================== */}

                    <div className='FormComponent-Row'>
                        <label className='font-medium  mt-0'> Request ID </label>
                        <input
                            className='Input-Field-Text'
                            style={{ width: '300px' }}
                            type='text'
                            defaultValue={task?.requestid.toUpperCase()}
                            disabled
                        ></input>
                    </div>

                    {/* ==================================== */}
                    {/*  Assignee                            */}
                    {/* ==================================== */}

                    <div className='FormComponent-Row'>
                        <label className='font-medium'> Assignee </label>
                        {
                            task !== undefined &&
                            <AssignTask
                                query={['roles.procurementUser', '==', true]}
                                currentRequest={task}
                                styleInput={{ maxWidth: '300px', borderRadius: '5px' }}
                                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled') ? true : false}
                            ></AssignTask>
                        }
                    </div>

                    {/* =========================================================== */}
                    {/*  Date Assigned                                              */}
                    {/* =========================================================== */}

                    <div className='FormComponent-Row mb-2'>
                        <label className='font-medium'> Date Assigned </label>
                        <input
                            className='Input-Field-Text'
                            style={{ width: '300px' }}
                            type='text'
                            defaultValue={ConvertDate(task?.assigneddate)}
                            disabled
                        ></input>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Digital Commercial Request                           */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'digitalCommercialRequestCollapsed': !taskSection.digitalCommercialRequestCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.digitalCommercialRequestCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'> Commercial Request </h4>
                </div>
                <hr className='m-0' hidden={taskSection?.digitalCommercialRequestCollapsed}></hr>

                {/* ------------- COMMERCIAL REQUEST FORM ------------- */}

                {/* ---------------------------------------------------------- */}
                {/*  Toggle between Digital/Demand Commercial Request Form     */}
                {/* ---------------------------------------------------------- */}

                {
                    task?.digitalcommercialrequest?.requestorigin === 'Digital' ?

                    // DIGITAL COMMERCIAL REQUEST FORM
                    <div className='p-[3%]' hidden={taskSection?.digitalCommercialRequestCollapsed}>

                        {/* ------------- REQUEST INFORMATION ------------- */}

                        <h5 className='text-[18px] text-teal-600 mb-2'>Request Information</h5>

                        {/* =========================================================== */}
                        {/*  1. PoA Number                                              */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 1. PoA Number </label>
                            <input
                                className='Input-Field-Text uppercase'
                                style={{ width: '200px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.poanumber}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  2. Request Type                                            */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 2. Request Type </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.requesttype}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  3. Application Name                                        */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 3. Application Name </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.applicationname === '' || task?.digitalcommercialrequest?.applicationname === undefined ? '-' : task?.digitalcommercialrequest?.applicationname}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  4. Short Description                                       */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 4. Short Description </label>
                            <textarea
                                className='Input-Field-TextArea'
                                type='text'
                                value={task?.digitalcommercialrequest?.shortdescription}
                                disabled
                            ></textarea>
                        </div>

                        {/* =========================================================== */}
                        {/*  5. What is the problem you are trying to solve?            */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 5. What is the problem you are trying to solve? </label>
                            <textarea
                                className='Input-Field-TextArea'
                                type='text'
                                value={task?.digitalcommercialrequest?.problemtosolve}
                                disabled
                            ></textarea>
                        </div>


                        {/* =========================================================== */}
                        {/*  6. What is the outcome you are looking for?                */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 6. What is the outcome you are looking for? </label>
                            <textarea
                                className='Input-Field-TextArea'
                                type='text'
                                value={task?.digitalcommercialrequest?.expectedoutcome}
                                disabled
                            ></textarea>
                        </div>  

                        {/* =========================================================== */}
                        {/*  7. What would be the impact if this didn't go ahead?       */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 7. What would be the impact if this didn't go ahead? </label>
                            <textarea
                                className='Input-Field-TextArea'
                                type='text'
                                value={task?.digitalcommercialrequest?.expectedimpact}
                                disabled
                            ></textarea>
                        </div>

                        {/* =========================================================== */}
                        {/*  8. Requester Name                                          */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 8. Requester Name </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.requestername}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  9. Region                                                  */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 9. Region </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '200px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.region}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  10. Sponsor Name                                           */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 10. Sponsor Name </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.sponsorname?.email}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  11. Created By                                             */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 11. Created By </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '300px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.createdby?.email}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  12. Supporting Documents                                   */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 12. Supporting Documents </label>
                            {
                                task?.digitalcommercialrequest?.supportingdocs?.length === 0 ?
                                    (
                                        <div>
                                            No documents found.
                                        </div>
                                    )
                                    :
                                    (
                                        <table className='table-auto w-full border-separate border-spacing-0 border-1 border-solid border-[#D8D8D8] rounded-[10px]'>
                                            <thead>
                                                <tr>
                                                    <th className='font-normal px-3 py-2'>Uploaded Documents</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    task?.digitalcommercialrequest?.supportingdocs?.map((file, index) => (
                                                        <tr key={index}>
                                                            <td className='flex flex-row gap-2 px-3 py-2 text-[#0972D3] underline border-t border-t-[#D8D8D8]'>
                                                                <img src={FileIcon} alt='file'></img>
                                                                <a href={file?.fileUrl} target='_blank' rel='noreferrer'>
                                                                    {file?.fileName}
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    )
                            }
                        </div>

                        {/* ------------- CONTRACT INFORMATION ------------- */}

                        <h5 className='text-[18px] text-teal-600 mt-4 mb-2'>Contract Information</h5>

                        {/* =========================================================== */}
                        {/*  13. Commercial Scope                                       */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium mb-0'> 13. Commercial Scope Description </label>
                            <textarea
                                className='Input-Field-TextArea'
                                value={task?.digitalcommercialrequest?.commercialscope}
                                disabled
                            ></textarea>
                        </div>

                        {/* =========================================================== */}
                        {/*  14. Urgency                                                */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 14. Urgency </label>
                            <select className='Input-Field-Select' style={{ width: '200px' }} disabled>
                                <option>{task?.digitalcommercialrequest?.urgency}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  15. Impact                                                 */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 15. Impact </label>
                            <select className='Input-Field-Select' style={{ width: '200px' }} disabled>
                                <option>{task?.digitalcommercialrequest?.impact}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  16. Buy / Sell Agreement                                   */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 16. Is this a buy or sell agreement? </label>
                            <select className='Input-Field-Select' style={{ width: '200px' }} disabled>
                                <option>{task?.digitalcommercialrequest?.buysellagreement}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  17. Agreement Type                                         */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 17. Agreement Type </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.agreementtype}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  18. Lendlease Company                                      */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 18. Lendlease Company (Signing Entity) </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.lendleasecompany}</option>
                            </select>
                        </div>

                        {/* ------------- OTHER PARTY INFORMATION (VENDOR/CUSTOMER) ------------- */}

                        <h5 className='text-[18px] text-teal-600 mt-4 mb-2'> Other Party Information (Vendor / Customer) </h5>

                        {/* =========================================================== */}
                        {/*  19. Vendor Name                                            */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 19. Vendor Name </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.vendor?.vendorname}
                                disabled
                            ></input>
                        </div>

                        {/* ------------- FINANCIAL INFORMATION ------------- */}

                        <h5 className='text-[18px] text-teal-600 mt-4 mb-2' hidden={task?.digitalcommercialrequest?.requesttype === 'Establish NDA'}> Financial Information </h5>

                        {/* =========================================================== */}
                        {/*  20. Budget Owner                                           */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row' hidden={task?.digitalcommercialrequest?.requesttype === 'Establish NDA'}>
                            <label className='font-medium'> 20. Budget Owner </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.budgetowner?.givenname} {task?.digitalcommercialrequest?.budgetowner?.surname}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  21. Finance Manager                                        */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row' hidden={task?.digitalcommercialrequest?.requesttype === 'Establish NDA'}>
                            <label className='font-medium'> 21. Finance Manager </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.financemanager?.givenname}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  22. Currency                                               */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row' hidden={task?.digitalcommercialrequest?.requesttype === 'Establish NDA' || task?.digitalcommercialrequest?.requesttype === 'Assign/Novate Agreement' || task?.digitalcommercialrequest?.requesttype === 'Terminate Existing Agreement'}>
                            <label className='font-medium'> 22. Currency </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.currency === '' || task?.digitalcommercialrequest?.currency === undefined ? '-' : task?.digitalcommercialrequest?.currency}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  23. Estimated Annual Contract Value (exGST)                 */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row' hidden={task?.digitalcommercialrequest?.requesttype === 'Establish NDA' || task?.digitalcommercialrequest?.requesttype === 'Assign/Novate Agreement' || task?.digitalcommercialrequest?.requesttype === 'Terminate Existing Agreement'}>
                            <label className='font-medium'> 23. Estimated Annual Contract Value (exGST) </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '300px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.estannualcontractvalue === '' || task?.digitalcommercialrequest?.estannualcontractvalue === undefined ? '-' : task?.digitalcommercialrequest?.estannualcontractvalue}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  24. Estimated Total Contract Value (exGST)                  */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row' hidden={task?.digitalcommercialrequest?.requesttype === 'Establish NDA' || task?.digitalcommercialrequest?.requesttype === 'Assign/Novate Agreement' || task?.digitalcommercialrequest?.requesttype === 'Terminate Existing Agreement'}>
                            <label className='font-medium'> 24. Estimated Total Contract Value (exGST) </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '300px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.esttotalcontractvalue === '' || task?.digitalcommercialrequest?.esttotalcontractvalue === undefined ? '-' : task?.digitalcommercialrequest?.esttotalcontractvalue}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  25. Recharge Information                                   */}
                        {/* =========================================================== */}
            
                        <div className='FormComponent-Row' hidden={task?.digitalcommercialrequest?.requesttype === 'Establish NDA' || task?.digitalcommercialrequest?.requesttype === 'Assign/Novate Agreement'}>
                            <label><b style={{ fontWeight: '500' }}> {task?.requesttype === 'Terminate Existing Agreement' ? 22 : 25}. Recharge Information </b> </label>

                            <p><i>Project code, task number and expenditure type allow the recharge of technology costs.</i></p>

                            {/* Project Code */}
                            <div className='flex flex-row'>
                                <label className='w-[130px]'>Project Code</label>
                                <input
                                    className='Input-Field-Text'
                                    type='text'
                                    placeholder={'Project Code'}
                                    value={task?.digitalcommercialrequest?.projectcode === '' || task?.digitalcommercialrequest?.projectcode === undefined ? '-' : task?.digitalcommercialrequest?.projectcode}
                                    disabled={true}
                                ></input>
                            </div>

                            {/* Task Number */}
                            <div className='flex flex-row'>
                                <label className='w-[130px]'>Task Number</label>
                                <input
                                    className='Input-Field-Text'
                                    type='text'
                                    placeholder={'Task Number'}
                                    value={task?.digitalcommercialrequest?.tasknumber === '' || task?.digitalcommercialrequest?.tasknumber === undefined ? '-' : task?.digitalcommercialrequest?.tasknumber}
                                    disabled={true}
                                ></input>
                            </div>

                            {/* Expenditure Type */}
                            <div className='flex flex-row'>
                                <label className='w-[130px]'>Expenditure Type</label>
                                <input
                                    className='Input-Field-Text'
                                    type='text'
                                    placeholder={'Expenditure Type'}
                                    value={task?.digitalcommercialrequest?.expendituretype === '' || task?.digitalcommercialrequest?.expendituretype === undefined ? '-' : task?.digitalcommercialrequest?.expendituretype}
                                    disabled={true}
                                ></input>
                            </div>
                        </div>

                    </div>

                    :
                    // DEMAND COMMERCIAL REQUEST FORM
                    <div className='p-[3%]' hidden={taskSection?.digitalCommercialRequestCollapsed}>

                        {/* ------------- REQUEST INFORMATION ------------- */}

                        <h5 className='text-[18px] text-teal-600 mb-2'>Request Information</h5>

                        {/* =========================================================== */}
                        {/*  1. Customer Request ID                                     */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 1. Customer Request ID </label>
                            <input
                                className='Input-Field-Text uppercase'
                                style={{ width: '200px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.parentrequestid}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  2. PoA Number                                              */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 2. PoA Number </label>
                            <input
                                className='Input-Field-Text uppercase'
                                style={{ width: '200px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.poanumber}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  3. Request Type                                            */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 3. Request Type </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.requesttype}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  4. Application Name                                        */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 4. Application Name </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.applicationname}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  5. Short Description                                       */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 5. Short Description </label>
                            <textarea
                                className='Input-Field-TextArea'
                                type='text'
                                value={task?.digitalcommercialrequest?.shortdescription}
                                disabled
                            ></textarea>
                        </div>

                        {/* =========================================================== */}
                        {/*  6. Requester Name                                          */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 6. Requester Name </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.requestername}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  7. Region                                                  */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 7. Region </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '200px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.region}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  8. Sponsor Name                                            */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 8. Sponsor Name </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.sponsorname?.email}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  9. Created By                                              */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 9. Created By </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '300px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.createdby?.email}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  10. Supporting Documents                                   */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 10. Supporting Documents </label>
                            {
                                task?.digitalcommercialrequest?.supportingdocs?.length === 0 ?
                                    (
                                        <div>
                                            No documents found.
                                        </div>
                                    )
                                    :
                                    (
                                        <table className='table-auto w-full border-separate border-spacing-0 border-1 border-solid border-[#D8D8D8] rounded-[10px]'>
                                            <thead>
                                                <tr>
                                                    <th className='font-normal px-3 py-2'>Uploaded Documents</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    task?.digitalcommercialrequest?.supportingdocs?.map((file, index) => (
                                                        <tr key={index}>
                                                            <td className='flex flex-row gap-2 px-3 py-2 text-[#0972D3] underline border-t border-t-[#D8D8D8]'>
                                                                <img src={FileIcon} alt='file'></img>
                                                                <a href={file?.fileUrl} target='_blank' rel='noreferrer'>
                                                                    {file?.fileName}
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    )
                            }
                        </div>

                        {/* ------------- CONTRACT INFORMATION ------------- */}

                        <h5 className='text-[18px] text-teal-600 mt-4 mb-2'>Contract Information</h5>

                        {/* =========================================================== */}
                        {/*  11. Commercial Scope                                       */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium mb-0'> 11. Commercial Scope Description </label>
                            <textarea
                                className='Input-Field-TextArea'
                                value={task?.digitalcommercialrequest?.commercialscope}
                                disabled
                            ></textarea>
                        </div>

                        {/* =========================================================== */}
                        {/*  12. Urgency                                                */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 12. Urgency </label>
                            <select className='Input-Field-Select' style={{ width: '200px' }} disabled>
                                <option>{task?.digitalcommercialrequest?.urgency}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  13. Impact                                                 */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 13. Impact </label>
                            <select className='Input-Field-Select' style={{ width: '200px' }} disabled>
                                <option>{task?.digitalcommercialrequest?.impact}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  14. Buy / Sell Agreement                                   */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 14. Is this a buy or sell agreement? </label>
                            <select className='Input-Field-Select' style={{ width: '200px' }} disabled>
                                <option>{task?.digitalcommercialrequest?.buysellagreement}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  15. Agreement Type                                         */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 15. Agreement Type </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.agreementtype}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  16. Lendlease Company                                      */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 16. Lendlease Company (Signing Entity) </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.lendleasecompany}</option>
                            </select>
                        </div>

                        {/* ------------- OTHER PARTY INFORMATION (VENDOR/CUSTOMER) ------------- */}

                        <h5 className='text-[18px] text-teal-600 mt-4 mb-2'> Other Party Information (Vendor / Customer) </h5>

                        {/* =========================================================== */}
                        {/*  17. Vendor Name                                            */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 17. Vendor Name </label>
                            <input
                                className='Input-Field-Text'
                                type='text'
                                value={task?.digitalcommercialrequest?.vendor?.vendorname}
                                disabled
                            ></input>
                        </div>

                        {/* ------------- FINANCIAL INFORMATION ------------- */}

                        <h5 className='text-[18px] text-teal-600 mt-4 mb-2'> Financial Information </h5>

                        {/* =========================================================== */}
                        {/*  18. Currency                                               */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 18. Currency </label>
                            <select className='Input-Field-Select' disabled>
                                <option>{task?.digitalcommercialrequest?.currency === '' || task?.digitalcommercialrequest?.currency === undefined ? '-' : task?.digitalcommercialrequest?.currency}</option>
                            </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  19. Estimated Annual Contract Value (exGST)                 */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 19. Estimated Annual Contract Value (exGST) </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '300px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.estannualcontractvalue === '' || task?.digitalcommercialrequest?.estannualcontractvalue === undefined ? '-' : task?.digitalcommercialrequest?.estannualcontractvalue}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  20. Estimated Total Contract Value (exGST)                  */}
                        {/* =========================================================== */}

                        <div className='FormComponent-Row'>
                            <label className='font-medium'> 20. Estimated Total Contract Value (exGST) </label>
                            <input
                                className='Input-Field-Text'
                                style={{ width: '300px' }}
                                type='text'
                                value={task?.digitalcommercialrequest?.esttotalcontractvalue === '' || task?.digitalcommercialrequest?.esttotalcontractvalue === undefined ? '-' : task?.digitalcommercialrequest?.esttotalcontractvalue}
                                disabled
                            ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  21. Recharge Information                                   */}
                        {/* =========================================================== */}
            
                        <div className='FormComponent-Row'>
                            <label><b style={{ fontWeight: '500' }}> 21. Recharge Information </b> </label>

                            <p><i>Project code, task number and expenditure type allow the recharge of technology costs.</i></p>

                            {/* Project Code */}
                            <div className='flex flex-row'>
                                <label className='w-[130px]'>Project Code</label>
                                <input
                                    className='Input-Field-Text'
                                    type='text'
                                    placeholder={'Project Code'}
                                    value={task?.digitalcommercialrequest?.projectcode === '' || task?.digitalcommercialrequest?.projectcode === undefined ? '-' : task?.digitalcommercialrequest?.projectcode}
                                    disabled={true}
                                ></input>
                            </div>

                            {/* Task Number */}
                            <div className='flex flex-row'>
                                <label className='w-[130px]'>Task Number</label>
                                <input
                                    className='Input-Field-Text'
                                    type='text'
                                    placeholder={'Task Number'}
                                    value={task?.digitalcommercialrequest?.tasknumber === '' || task?.digitalcommercialrequest?.tasknumber === undefined ? '-' : task?.digitalcommercialrequest?.tasknumber}
                                    disabled={true}
                                ></input>
                            </div>

                            {/* Expenditure Type */}
                            <div className='flex flex-row'>
                                <label className='w-[130px]'>Expenditure Type</label>
                                <input
                                    className='Input-Field-Text'
                                    type='text'
                                    placeholder={'Expenditure Type'}
                                    value={task?.digitalcommercialrequest?.expendituretype === '' || task?.digitalcommercialrequest?.expendituretype === undefined ? '-' : task?.digitalcommercialrequest?.expendituretype}
                                    disabled={true}
                                ></input>
                            </div>
                        </div>

                    </div>

                }

            </div>

            {/* =========================================================== */}
            {/*  Cancel Request Modal                                       */}
            {/* =========================================================== */}

            <CancelRequestModal
                cancelRequestModal={cancelRequestModal}
                setCancelRequestModal={setCancelRequestModal}
                task={task}
            ></CancelRequestModal>

        </div >
    );
}
